import { useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { date, isoDate, money } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { Field, FormGrid, Input, Select, Textarea } from '@/components/ui/form'
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  MilestoneSummary,
  PaymentBasis,
  PaymentListItem,
  PaymentState,
  QuotationPayments as Payments,
} from '@/types/api'

/**
 * What the customer agreed to pay, and what actually arrived.
 *
 * <p>
 * One tab rather than two, because they are the same question asked twice and the
 * answer - how much is still outstanding - only exists when both are on the screen.
 * </p>
 * <p>
 * Nothing here shows a commission figure, for anybody. Commission lives on its own
 * destination behind <code>reseller.commission.view</code>, so a partner's rates
 * cannot be read off a deal screen over somebody's shoulder.
 * </p>
 */
export function QuotationPayments({
  quotationId,
  milestones,
  canRecord,
}: {
  quotationId: string
  milestones: readonly MilestoneSummary[]
  /** False once the quotation is not in a state that accepts money. */
  canRecord: boolean
}) {
  const { can } = useAuth()
  const [recording, setRecording] = useState(false)
  const [reversing, setReversing] = useState<PaymentListItem | undefined>()

  const payments = useAsync<Payments>(
    (signal) => api.get<Payments>(`/quotations/${quotationId}/payments`, undefined, signal),
    [quotationId],
  )

  if (payments.loading && !payments.data) return <Loading />
  if (payments.error) {
    return (
      <ErrorState
        title={payments.error.title}
        detail={payments.error.detail}
        onRetry={payments.reload}
      />
    )
  }

  const data = payments.data
  if (!data) return null

  const symbol = data.currencyCode
  const decimals = data.currencyDecimals
  const recordable = canRecord && can('quotation.payment.record')

  // A milestone is settled when a payment that has not been reversed points at it.
  // Derived rather than stored: two records of the same fact drift, and the payments
  // are the record that decides.
  const settled = new Set(
    data.payments
      .filter((payment) => payment.milestoneId !== null && !payment.isReversed && !payment.reversalOfPaymentId)
      .map((payment) => payment.milestoneId as string),
  )

  return (
    <>
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Figure label="Scheduled" value={money(data.scheduled, symbol, decimals)} hint={`${milestones.length} milestone${milestones.length === 1 ? '' : 's'}`} />
        <Figure label="Received" value={money(data.collected, symbol, decimals)} hint={countLabel(data.payments)} tone="pos" />
        <Figure
          label="Outstanding"
          value={money(data.outstanding, symbol, decimals)}
          hint={<PaymentStateBadge state={data.state} />}
        />
      </div>

      <Card className="mb-5">
        <CardHeader
          title="Payments received"
          hint="Recorded, never inferred. A correction is a reversal, never an edit."
          action={
            recordable ? (
              <Button variant="primary" onClick={() => setRecording(true)}>
                Record a payment
              </Button>
            ) : undefined
          }
        />

        {data.payments.length === 0 ? (
          <EmptyState
            title="Nothing received yet"
            hint={
              recordable
                ? 'Record a payment when the money arrives. The date is the date it arrived, not today.'
                : 'Recording a payment sits with the Director of Sales and the Owner.'
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Received</Th>
                <Th>Basis</Th>
                <Th>Against</Th>
                <Th>Method</Th>
                <Th>Reference</Th>
                <Th numeric>Amount</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {data.payments.map((payment) => (
                <Tr key={payment.id}>
                  <Td>{date(payment.paidOn)}</Td>
                  <Td>{payment.basis === 'OneTime' ? 'One-time' : 'Recurring'}</Td>
                  <Td>{payment.milestoneName ?? <span className="text-faint">&mdash;</span>}</Td>
                  <Td>{payment.method ?? <span className="text-faint">&mdash;</span>}</Td>
                  <Td className="font-mono text-[12px]">
                    {payment.reference ?? <span className="text-faint">&mdash;</span>}
                  </Td>
                  <Td numeric className={payment.amount < 0 ? 'text-crit' : undefined}>
                    {money(payment.amount, symbol, decimals)}
                  </Td>
                  <Td className="text-right">
                    {payment.reversalOfPaymentId ? (
                      <Badge tone="critical">Reversal</Badge>
                    ) : payment.isReversed ? (
                      <Badge>Reversed</Badge>
                    ) : can('quotation.payment.reverse') ? (
                      <Button onClick={() => setReversing(payment)}>Reverse</Button>
                    ) : null}
                  </Td>
                </Tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <Td className="font-medium" colSpan={5}>
                  Received
                </Td>
                <Td numeric className="font-medium">
                  {money(data.collected, symbol, decimals)}
                </Td>
                <Td />
              </tr>
            </tfoot>
          </Table>
        )}
      </Card>

      <RecordPayment
        open={recording}
        quotationId={quotationId}
        currencyCode={symbol}
        decimals={decimals}
        milestones={milestones.filter((milestone) => !settled.has(milestone.id))}
        onClose={() => setRecording(false)}
        onDone={() => {
          setRecording(false)
          payments.reload()
        }}
      />

      <ReversePayment
        quotationId={quotationId}
        payment={reversing}
        currencyCode={symbol}
        decimals={decimals}
        onClose={() => setReversing(undefined)}
        onDone={() => {
          setReversing(undefined)
          payments.reload()
        }}
      />
    </>
  )
}

function countLabel(payments: readonly PaymentListItem[]): string {
  const settled = payments.filter((p) => !p.reversalOfPaymentId).length
  return `${settled} payment${settled === 1 ? '' : 's'}`
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: React.ReactNode
  tone?: 'pos'
}) {
  return (
    <Card className="px-5 py-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">{label}</p>
      <p className={`tabular mt-1.5 text-[22px] font-semibold ${tone === 'pos' ? 'text-pos' : ''}`}>
        {value}
      </p>
      <div className="mt-1 text-[12px] text-muted">{hint}</div>
    </Card>
  )
}

/**
 * Where the deal stands on money, beside where it stands on winning.
 *
 * <p>
 * Two axes, two badges. A quotation is Won and unpaid, Won and part paid, Won and
 * paid - one enum cannot hold both without one of them lying.
 * </p>
 */
export function PaymentStateBadge({ state }: { state: PaymentState }) {
  switch (state) {
    case 'Paid':
      return <Badge tone="positive">Paid</Badge>
    case 'PartiallyPaid':
      return <Badge tone="warning">Part paid</Badge>
    case 'Overpaid':
      return <Badge tone="accent">Overpaid</Badge>
    default:
      return <Badge>Unpaid</Badge>
  }
}

function RecordPayment({
  open,
  quotationId,
  currencyCode,
  decimals,
  milestones,
  onClose,
  onDone,
}: {
  open: boolean
  quotationId: string
  currencyCode: string
  decimals: number
  milestones: readonly MilestoneSummary[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [paidOn, setPaidOn] = useState(isoDate())
  const [amount, setAmount] = useState('')
  const [milestoneId, setMilestoneId] = useState('')
  const [basis, setBasis] = useState<PaymentBasis>('OneTime')
  const [method, setMethod] = useState('Bank transfer')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  /**
   * Picking a milestone fills in the basis and the amount.
   *
   * <p>
   * The common case, and most of why the schedule was worth having: the milestone
   * already knows which half of the deal it divides, so nobody has to decide it twice.
   * Both stay editable, because a payment that settles no milestone still has to be
   * recordable.
   * </p>
   */
  function chooseMilestone(id: string) {
    setMilestoneId(id)
    const milestone = milestones.find((m) => m.id === id)
    if (!milestone) return
    setBasis(milestone.basis === 'SubscriptionAdvance' ? 'Recurring' : 'OneTime')
    setAmount(String(milestone.amount))
  }

  async function record() {
    setSaving(true)
    try {
      await api.post(`/quotations/${quotationId}/payments`, {
        paidOn,
        amount: Number(amount),
        basis,
        milestoneId: milestoneId === '' ? null : milestoneId,
        method: method.trim() === '' ? null : method.trim(),
        reference: reference.trim() === '' ? null : reference.trim(),
        note: note.trim() === '' ? null : note.trim(),
      })
      toast.show('Payment recorded', 'success')
      onDone()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? cause.message : 'Could not record the payment',
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Record a payment"
      description="The date is the date the money arrived, which decides the period it accrues commission in."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={record} disabled={saving || amount.trim() === ''}>
            {saving ? 'Recording...' : 'Record'}
          </Button>
        </>
      }
    >
      <FormGrid>
        <Field label="Date received" required>
          {(id) => (
            <Input id={id} type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          )}
        </Field>
        <Field label={`Amount (${currencyCode})`} required>
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="What it settles"
          hint="Optional. Choosing one fills in the basis and the amount."
        >
          {(id) => (
            <Select id={id} value={milestoneId} onChange={(e) => chooseMilestone(e.target.value)}>
              <option value="">Nothing on the schedule</option>
              {milestones.map((milestone) => (
                <option key={milestone.id} value={milestone.id}>
                  {milestone.name} &mdash; {money(milestone.amount, currencyCode, decimals)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Basis"
          required
          hint="Recorded, never inferred: a mixed deal receiving one figure has no safe automatic answer."
        >
          {(id) => (
            <Select
              id={id}
              value={basis}
              onChange={(e) => setBasis(e.target.value as PaymentBasis)}
            >
              <option value="OneTime">One-time</option>
              <option value="Recurring">Recurring</option>
            </Select>
          )}
        </Field>
        <Field label="Method">
          {(id) => <Input id={id} value={method} onChange={(e) => setMethod(e.target.value)} />}
        </Field>
        <Field label="Reference">
          {(id) => (
            <Input id={id} value={reference} onChange={(e) => setReference(e.target.value)} />
          )}
        </Field>
      </FormGrid>
      <Field label="Note">
        {(id) => <Textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
    </Dialog>
  )
}

function ReversePayment({
  quotationId,
  payment,
  currencyCode,
  decimals,
  onClose,
  onDone,
}: {
  quotationId: string
  payment?: PaymentListItem
  currencyCode: string
  decimals: number
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  async function reverse() {
    if (!payment) return
    setSaving(true)
    try {
      await api.post(`/quotations/${quotationId}/payments/${payment.id}/reverse`, { reason })
      toast.show('Payment reversed', 'success')
      onDone()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? cause.message : 'Could not reverse the payment',
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={payment !== undefined}
      title="Reverse this payment"
      description="Writes a second row carrying the negative amount, so every figure downstream stays a plain sum. Nothing is edited and nothing is deleted."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={reverse} disabled={saving || reason.trim() === ''}>
            {saving ? 'Reversing...' : 'Reverse'}
          </Button>
        </>
      }
    >
      {payment && (
        <p className="mb-4 rounded-md bg-warn-wash px-4 py-3 text-[13px] text-warn">
          {money(payment.amount, currencyCode, decimals)} received {date(payment.paidOn)}. The
          reversal carries that same date, so it lands in the period the money did &mdash; and it
          takes the commission it accrued back off the partner by the same arithmetic.
        </p>
      )}
      <Field label="Why" required hint="Goes on the audit record. Somebody will read this later.">
        {(id) => <Textarea id={id} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </Dialog>
  )
}
